import React, { useState, useEffect, useCallback } from 'react';
import { FiRefreshCw, FiAlertCircle, FiClock, FiActivity } from 'react-icons/fi';
import {
  getFabricRuntimeRuns,
  getFabricRuntimeWorkspaces,
  triggerFabricRuntimeSync,
  type FabricRun,
  type RuntimeStats,
  type RunStatus,
} from '../../api/fabric';

// ─── Gradient map ────────────────────────────────────────────────────────────

const COLOR_GRADIENT: Record<string, string> = {
  '#6366F1': 'linear-gradient(135deg, #4338CA 0%, #818CF8 100%)',
  '#7C3AED': 'linear-gradient(135deg, #5B21B6 0%, #8B5CF6 100%)',
  '#2563EB': 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)',
  '#059669': 'linear-gradient(135deg, #065F46 0%, #10B981 100%)',
  '#0D9488': 'linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)',
  '#DC2626': 'linear-gradient(135deg, #991B1B 0%, #EF4444 100%)',
  '#D97706': 'linear-gradient(135deg, #92400E 0%, #F59E0B 100%)',
};

// ─── Config ───────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<RunStatus, { label: string; color: string; bg: string }> = {
  Succeeded: { label: 'Succès',    color: '#059669', bg: '#ECFDF5' },
  Failed:    { label: 'Échec',     color: '#DC2626', bg: '#FEF2F2' },
  Running:   { label: 'En cours',  color: '#2563EB', bg: '#EFF6FF' },
  Cancelled: { label: 'Annulé',   color: '#6B7280', bg: '#F9FAFB' },
  Deduped:   { label: 'Dédupliqué',color: '#9CA3AF', bg: '#F3F4F6' },
  Pending:   { label: 'En attente',color: '#D97706', bg: '#FEF3C7' },
};

const TYPE_ICONS: Record<string, string> = {
  pipeline: '/icons/fabric/pipeline_64_item.svg',
  notebook: '/icons/fabric/notebook_64_item.svg',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDuration(secs: number | null): string {
  if (!secs) return '—';
  if (secs < 60)  return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ${secs % 60}s`;
  return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

function timeAgo(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)   return 'À l\'instant';
  if (mins < 60)  return `il y a ${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `il y a ${hrs}h`;
  return `il y a ${Math.floor(hrs / 24)}j`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: RunStatus }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: '#6B7280', bg: '#F3F4F6' };
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
      color: cfg.color, background: cfg.bg,
    }}>
      {cfg.label}
    </span>
  );
}

function MetricCard({ label, value, sub, color, icon }: {
  label: string; value: number | string; sub?: string; color: string; icon: React.ReactNode;
}) {
  const gradient = COLOR_GRADIENT[color] || `linear-gradient(135deg, ${color} 0%, ${color} 100%)`;
  return (
    <div style={{
      flex: 1, minWidth: 140, padding: '1.25rem',
      background: gradient,
      borderRadius: '0.875rem',
      color: 'white',
      boxShadow: `0 4px 16px ${color}45`,
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', top: -16, right: -16, width: 64, height: 64, background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
        <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1 }}>{value}</div>
        <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '0.5rem', padding: '0.35rem' }}>{icon}</div>
      </div>
      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function RunRow({ run }: { run: FabricRun }) {
  const [expanded, setExpanded] = useState(false);
  const hasError = !!run.error_message;

  return (
    <>
      <tr
        onClick={() => hasError && setExpanded(e => !e)}
        style={{
          cursor: hasError ? 'pointer' : 'default',
          background: expanded ? '#FEF2F2' : undefined,
        }}
      >
        <td style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <img
            src={TYPE_ICONS[run.item_type] || ''}
            alt="" style={{ width: 16, height: 16 }}
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{run.item_name}</div>
            <div style={{ fontSize: 11, color: '#9CA3AF' }}>{run.item_type}</div>
          </div>
        </td>
        <td style={{ padding: '10px 14px', fontSize: 12, color: '#6B7280' }}>{run.workspace_name || run.workspace_id}</td>
        <td style={{ padding: '10px 14px' }}><StatusBadge status={run.status as RunStatus} /></td>
        <td style={{ padding: '10px 14px', fontSize: 12, color: '#374151' }}>
          <div>{formatDate(run.started_at)}</div>
          <div style={{ fontSize: 10, color: '#9CA3AF' }}>{timeAgo(run.started_at)}</div>
        </td>
        <td style={{ padding: '10px 14px', fontSize: 12, color: '#374151', fontFamily: 'monospace' }}>
          {formatDuration(run.duration_seconds)}
        </td>
        <td style={{ padding: '10px 14px', fontSize: 11, color: '#DC2626', maxWidth: 220 }}>
          {hasError && !expanded && (
            <span style={{ opacity: 0.7 }}>⚠ {run.error_message!.substring(0, 60)}{run.error_message!.length > 60 ? '…' : ''}</span>
          )}
          {hasError && <span style={{ marginLeft: 4, fontSize: 10, color: '#9CA3AF' }}>{expanded ? '▲' : '▼'}</span>}
        </td>
      </tr>
      {expanded && hasError && (
        <tr>
          <td colSpan={6} style={{ padding: '0 14px 12px 48px' }}>
            <div style={{
              padding: '10px 14px', background: '#FEF2F2', border: '1px solid #FCA5A5',
              borderRadius: 8, fontSize: 12, color: '#7F1D1D', fontFamily: 'monospace', whiteSpace: 'pre-wrap',
            }}>
              {run.error_message}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const RuntimeTab: React.FC = () => {
  const [runs, setRuns]               = useState<FabricRun[]>([]);
  const [stats, setStats]             = useState<RuntimeStats | null>(null);
  const [workspaces, setWorkspaces]   = useState<{ workspace_id: string; workspace_name: string }[]>([]);
  const [loading, setLoading]         = useState(false);
  const [syncing, setSyncing]         = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [lastSync, setLastSync]       = useState<string | null>(null);

  // Filtres
  const [filterType, setFilterType]   = useState<'all' | 'pipeline' | 'notebook'>('all');
  const [filterWs, setFilterWs]       = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Pagination
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage]         = useState(1);

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getFabricRuntimeRuns({
        type: filterType === 'all' ? undefined : filterType,
        workspaceId: filterWs || undefined,
        status: filterStatus || undefined,
        limit: 200,
        refresh,
      });
      setRuns(result.runs);
      setStats(result.stats);
      setLastSync(result.stats.lastSync);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [filterType, filterWs, filterStatus]);

  const loadWorkspaces = async () => {
    try {
      const res = await getFabricRuntimeWorkspaces();
      setWorkspaces(res.workspaces);
    } catch {}
  };

  useEffect(() => { load(); loadWorkspaces(); }, []);
  useEffect(() => { setPage(1); load(); }, [filterType, filterWs, filterStatus]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await triggerFabricRuntimeSync();
      await load(true);
      await loadWorkspaces();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSyncing(false);
    }
  };

  const failureRate = stats
    ? stats.pipelineRuns + stats.notebookRuns > 0
      ? Math.round(((stats.pipelineFailures + stats.notebookFailures) / (stats.pipelineRuns + stats.notebookRuns)) * 100)
      : 0
    : 0;

  return (
    <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111827' }}>
            Runtime Monitoring
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6B7280' }}>
            Historique des exécutions pipelines & notebooks Microsoft Fabric
            {lastSync && <span style={{ marginLeft: 8, fontSize: 11, color: '#9CA3AF' }}>• Sync {timeAgo(lastSync)}</span>}
          </p>
        </div>
        <button
          onClick={handleSync}
          disabled={syncing || loading}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 16px', borderRadius: 8, border: '1px solid #D1D5DB',
            background: 'white', cursor: syncing ? 'not-allowed' : 'pointer',
            fontSize: 13, color: '#374151', fontWeight: 600,
          }}
        >
          <FiRefreshCw size={14} style={{ animation: syncing ? 'spin 1s linear infinite' : 'none' }} />
          {syncing ? 'Sync…' : 'Sync maintenant'}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          padding: '12px 16px', background: '#FEF2F2', border: '1px solid #FCA5A5',
          borderRadius: 10, color: '#DC2626', fontSize: 13, display: 'flex', gap: 8,
        }}>
          <FiAlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
          {error}
        </div>
      )}

      {/* Metrics */}
      {stats && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <MetricCard label="Runs pipelines" value={stats.pipelineRuns}
            sub={`${stats.pipelineFailures} échec(s)`} color="#6366F1"
            icon={<img src="/icons/fabric/pipeline_64_item.svg" alt="" style={{ width: 22, height: 22 }} />} />
          <MetricCard label="Runs notebooks" value={stats.notebookRuns}
            sub={`${stats.notebookFailures} échec(s)`} color="#7C3AED"
            icon={<img src="/icons/fabric/notebook_64_item.svg" alt="" style={{ width: 22, height: 22 }} />} />
          <MetricCard label="En cours" value={stats.pipelineRunning + stats.notebookRunning}
            color="#2563EB" icon={<FiActivity size={22} />} />
          <MetricCard label="Taux d'échec" value={`${failureRate}%`}
            color={failureRate > 20 ? '#DC2626' : failureRate > 5 ? '#D97706' : '#059669'}
            icon={<FiAlertCircle size={22} />} />
          <MetricCard label="Durée moy. pipeline" value={formatDuration(stats.avgPipelineDuration)}
            color="#059669" icon={<FiClock size={22} />} />
          <MetricCard label="Durée moy. notebook" value={formatDuration(stats.avgNotebookDuration)}
            color="#0D9488" icon={<FiClock size={22} />} />
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Type */}
        <div style={{ display: 'flex', borderRadius: 8, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
          {(['all', 'pipeline', 'notebook'] as const).map(t => (
            <button key={t} onClick={() => setFilterType(t)} style={{
              padding: '6px 14px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
              background: filterType === t ? '#1E3A5F' : 'white',
              color: filterType === t ? 'white' : '#6B7280',
            }}>
              {t === 'all' ? 'Tous' : t === 'pipeline' ? '🔄 Pipelines' : '📓 Notebooks'}
            </button>
          ))}
        </div>

        {/* Workspace */}
        <select value={filterWs} onChange={e => setFilterWs(e.target.value)} style={{
          padding: '6px 10px', border: '1px solid #E5E7EB', borderRadius: 8,
          fontSize: 12, color: '#374151', background: 'white',
        }}>
          <option value="">Tous les workspaces</option>
          {workspaces.map(ws => (
            <option key={ws.workspace_id} value={ws.workspace_id}>{ws.workspace_name || ws.workspace_id}</option>
          ))}
        </select>

        {/* Status */}
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{
          padding: '6px 10px', border: '1px solid #E5E7EB', borderRadius: 8,
          fontSize: 12, color: '#374151', background: 'white',
        }}>
          <option value="">Tous les statuts</option>
          <option value="Succeeded">✅ Succès</option>
          <option value="Failed">❌ Échec</option>
          <option value="Running">⏳ En cours</option>
          <option value="Cancelled">🚫 Annulé</option>
        </select>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#6B7280' }}>
          <span>Lignes par page :</span>
          {[25, 50, 100].map(n => (
            <button key={n} onClick={() => { setPageSize(n); setPage(1); }} style={{
              padding: '4px 10px', borderRadius: 6, border: '1px solid #E5E7EB', cursor: 'pointer',
              fontSize: 12, fontWeight: 600,
              background: pageSize === n ? '#1E3A5F' : 'white',
              color: pageSize === n ? 'white' : '#374151',
            }}>{n}</button>
          ))}
          <span style={{ marginLeft: 4, color: '#9CA3AF' }}>
            {runs.length} run{runs.length > 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Table */}
      {loading && runs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div>
          <div>Chargement des runs…</div>
        </div>
      ) : runs.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '40px', color: '#9CA3AF',
          background: '#F9FAFB', borderRadius: 12, border: '1px dashed #D1D5DB',
        }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Aucun run trouvé</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>
            Cliquez sur "Sync maintenant" pour récupérer les derniers runs depuis Fabric.
          </div>
          <div style={{ fontSize: 11, color: '#D97706', marginTop: 8 }}>
            ⚠ Limité aux workspaces où le SP est membre direct
          </div>
        </div>
      ) : (() => {
        const totalPages = Math.max(1, Math.ceil(runs.length / pageSize));
        const safePage   = Math.min(page, totalPages);
        const pageRuns   = runs.slice((safePage - 1) * pageSize, safePage * pageSize);
        return (
          <>
            <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#F8FAFC', borderBottom: '1px solid #E5E7EB' }}>
                    {['Item', 'Workspace', 'Statut', 'Démarré', 'Durée', 'Erreur'].map(h => (
                      <th key={h} style={{
                        padding: '10px 14px', textAlign: 'left',
                        fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageRuns.map((run, idx) => (
                    <React.Fragment key={`${run.workspace_id}-${run.item_id}-${run.instance_id || idx}`}>
                      <RunRow run={run} />
                      {idx < pageRuns.length - 1 && (
                        <tr><td colSpan={6} style={{ height: 1, background: '#F3F4F6', padding: 0 }} /></tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination controls */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8 }}>
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  style={{
                    padding: '6px 14px', borderRadius: 8, border: '1px solid #D1D5DB',
                    background: 'white', cursor: safePage === 1 ? 'not-allowed' : 'pointer',
                    fontSize: 13, color: safePage === 1 ? '#D1D5DB' : '#374151',
                  }}
                >← Précédent</button>

                <span style={{ fontSize: 13, color: '#6B7280' }}>
                  Page {safePage} / {totalPages} — {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, runs.length)} sur {runs.length}
                </span>

                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  style={{
                    padding: '6px 14px', borderRadius: 8, border: '1px solid #D1D5DB',
                    background: 'white', cursor: safePage === totalPages ? 'not-allowed' : 'pointer',
                    fontSize: 13, color: safePage === totalPages ? '#D1D5DB' : '#374151',
                  }}
                >Suivant →</button>
              </div>
            )}
          </>
        );
      })()}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default RuntimeTab;
